import { useState } from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  TrainingRequestDetailFragment,
  TrainingRequestsEmployeesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { UpdateData } from 'modules/trainings/pages/MyTrainings/RequestDetail/types'

import EmployeeListItem from './EmployeeItem'

interface BlockProps {
  trainingRequest: TrainingRequestDetailFragment
  readonly: boolean
  form: FormikProps<UpdateData>
}

export const ParticipantsBlock = ({
  readonly,
  form,
  excludedEmployeeIds,
}: BlockProps & { excludedEmployeeIds: number[] }) => {
  const [open, toggle] = useToggle()
  const [openSelector, setOpenSelector] = useState<boolean>(false)

  const { data } = useQuery(TrainingRequestsEmployeesDocument)
  const employees =
    data?.employees.employees.filter((e) => !excludedEmployeeIds.includes(e.id)) ?? []
  const participants =
    data?.employees.employees.filter((e) => form.values.employeeIds.includes(e.id) ?? []) ?? []

  return (
    <>
      <CollapsableCard
        open={open}
        contentPadding="s24"
        Card={
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            <Box gap="s8" flexDirection="row">
              <Text.Normal>
                {i18n.t('trainings.training_requests.create.employee_selector_step')}
              </Text.Normal>
              <Box
                borderRadius={{ all: 'abs006' }}
                background="grey400"
                paddingX="s8"
                paddingY="s2"
              >
                <Text size="200" color="grey1000" weight="medium">
                  {form.values.employeeIds.length}
                </Text>
              </Box>
            </Box>
            <Box gap="s16" flexDirection="row">
              <Button
                disabled={readonly}
                onClick={() => setOpenSelector(!openSelector)}
                iconName="pencil"
                type="secondary"
              ></Button>
              <Button
                onClick={toggle(!open)}
                iconName={`${open ? 'chevron-up' : 'chevron-down'}`}
                type="secondary"
              ></Button>
            </Box>
          </Box>
        }
        Content={
          <Box
            maxHeight="s300"
            borderRadius={{ all: 'abs016' }}
            background="grey000White"
            hideScrollBar
            overflowY="scroll"
            border={
              participants.length > 0 && { all: { color: 'grey400', width: 's1', style: 'solid' } }
            }
            flexDirection="column"
          >
            {participants.map((employee) => (
              <EmployeeListItem
                employeeId={String(employee.id)}
                employeeName={employee.fullName}
                key={employee.id}
                attachment={employee.avatar?.url ?? ''}
              ></EmployeeListItem>
            ))}
          </Box>
        }
      ></CollapsableCard>
      {openSelector && (
        <Modal
          title={i18n.t('trainings.training_requests.create.employee_selector_step')}
          onClose={() => setOpenSelector(false)}
        >
          <Selector.Container
            searchPlaceholder={i18n.t('common.search')}
            blankLabel=""
            value={form.values.employeeIds}
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            onChange={(selection) =>
              form.setValues({ ...form.values, employeeIds: selection as number[] })
            }
            isMultiSelect
          >
            {employees.map((employee) => {
              return (
                <Selector.Option
                  key={employee.id}
                  value={employee.id}
                  label={employee.fullName}
                  searchValue={employee.fullName}
                >
                  {employee.fullName}
                </Selector.Option>
              )
            })}
          </Selector.Container>
        </Modal>
      )}
    </>
  )
}
